/**
 * Terra · carrusel principal y botón "Agregar" de las tarjetas de producto.
 */

/* ------------------------------------------------------------------ */
/* Carrusel                                                            */
/* ------------------------------------------------------------------ */
class TerraHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.terra-hero__slide'));
    this.dots = Array.from(this.querySelectorAll('.terra-hero__dot'));
    this.index = 0;
    this.delay = Number(this.dataset.speed || 5) * 1000;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && !this.reduced && this.slides.length > 1;

    this.dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        this.go(i);
        this.restart();
      })
    );

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    let startX = null;
    this.addEventListener('pointerdown', (e) => (startX = e.clientX));
    this.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      startX = null;
      if (Math.abs(dx) < 40) return;
      this.go(this.index + (dx < 0 ? 1 : -1));
      this.restart();
    });

    // En el editor de Shopify: mostrar la diapositiva que se está editando
    this.addEventListener('shopify:block:select', (e) => {
      const i = this.slides.indexOf(/** @type {HTMLElement} */ (e.target));
      if (i > -1) {
        this.stop();
        this.go(i);
      }
    });
    this.addEventListener('shopify:block:deselect', () => this.start());

    this.go(0);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  go(i) {
    if (!this.slides.length) return;
    this.index = (i + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, k) => {
      const on = k === this.index;
      slide.classList.toggle('is-active', on);
      slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      slide.querySelectorAll('a, button').forEach((el) => {
        if (on) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });
    this.dots.forEach((dot, k) => dot.setAttribute('aria-current', k === this.index ? 'true' : 'false'));
  }

  start() {
    if (!this.autoplay || this.timer) return;
    this.timer = window.setInterval(() => this.go(this.index + 1), this.delay);
  }

  stop() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('terra-hero')) customElements.define('terra-hero', TerraHero);

/* ------------------------------------------------------------------ */
/* Carrusel de una línea (Categorías): flechas y movimiento automático */
/* Solo actúa cuando las tarjetas no caben (formato carrusel).         */
/* ------------------------------------------------------------------ */
class TerraCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-terra-track]');
    if (!this.track) return;
    this.delay = Number(this.dataset.autoplay || 0) * 1000;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.paused = false;

    this.onClick = (event) => {
      const button = /** @type {HTMLElement} */ (event.target).closest('[data-terra-dir]');
      if (!button || !this.contains(button)) return;
      this.move(Number(button.getAttribute('data-terra-dir')), true);
      this.restart();
    };
    this.addEventListener('click', this.onClick);

    const pause = () => {
      this.paused = true;
      this.stop();
    };
    const resume = () => {
      this.paused = false;
      this.start();
    };
    this.addEventListener('mouseenter', pause);
    this.addEventListener('mouseleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
    this.track.addEventListener('touchstart', pause, { passive: true });
    this.track.addEventListener('touchend', () => window.setTimeout(resume, 4000), { passive: true });

    this.addEventListener('shopify:block:select', (event) => {
      pause();
      const target = /** @type {HTMLElement} */ (event.target);
      if (this.track.contains(target)) {
        const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
        const left =
          this.track.scrollLeft + target.getBoundingClientRect().left - this.track.getBoundingClientRect().left - pad;
        this.track.scrollTo({ left, behavior: 'smooth' });
      }
    });
    this.addEventListener('shopify:block:deselect', resume);

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.update();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.resizeObserver?.disconnect();
  }

  get scrollable() {
    return this.track.scrollWidth - this.track.clientWidth > 4;
  }

  update() {
    this.dataset.overflow = String(this.scrollable);
  }

  /** Ancho de una tarjeta más el espacio entre tarjetas */
  get step() {
    const first = /** @type {HTMLElement | undefined} */ (this.track.children[0]);
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first ? first.getBoundingClientRect().width + gap : this.track.clientWidth;
  }

  /**
   * @param {number} dir 1 = adelante, -1 = atrás
   * @param {boolean} page true = avanza lo que se ve en pantalla; false = una tarjeta
   */
  move(dir, page) {
    if (!this.scrollable) return;
    const step = this.step;
    const amount = page ? Math.max(step, Math.floor(this.track.clientWidth / step) * step) : step;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;
    let left;
    if (dir > 0) left = x >= max - 4 ? 0 : Math.min(x + amount, max);
    else left = x <= 4 ? max : Math.max(x - amount, 0);
    this.track.scrollTo({ left, behavior: this.reduced ? 'auto' : 'smooth' });
  }

  start() {
    if (!this.delay || this.reduced || this.timer || this.paused) return;
    this.timer = window.setInterval(() => {
      if (!document.hidden) this.move(1, false);
    }, this.delay);
  }

  stop() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('terra-carousel')) customElements.define('terra-carousel', TerraCarousel);

/* ------------------------------------------------------------------ */
/* Agregar al carrito desde las tarjetas Terra                         */
/* Avisa al carrito del tema para que el cajón se abra y se actualice. */
/* Si algo falla, el formulario se envía normal (va a /cart).          */
/* ------------------------------------------------------------------ */
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-terra-add]')) return;

  event.preventDefault();
  const button = form.querySelector('button[type="submit"]');
  button?.setAttribute('aria-busy', 'true');

  const formData = new FormData(form);
  const quantity = Number(formData.get('quantity') || 1);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let events = null;
  try {
    events = await import('@shopify/events');
  } catch (e) {
    events = null;
  }

  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  promise.catch(() => {});

  if (events && events.CartLinesUpdateEvent) {
    form.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity }],
        promise,
      })
    );
  }

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const data = await response.json();
    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    const failed = !response.ok || Boolean(data.status);

    if (events && events.CartLinesUpdateEvent) {
      resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'terra-product-card',
          sourceId: form.id,
          itemCount: quantity,
          didError: failed,
        },
      });
    } else if (!failed) {
      window.location.href = `${root}cart`;
      return;
    }

    if (failed) {
      button?.removeAttribute('aria-busy');
      const errorEl = form.querySelector('[data-terra-error]');
      if (errorEl) {
        errorEl.textContent = data.description || data.message || '';
        errorEl.hidden = false;
        window.setTimeout(() => (errorEl.hidden = true), 4000);
      }
      return;
    }

    if (button) {
      button.removeAttribute('aria-busy');
      button.classList.add('is-added');
      window.setTimeout(() => button.classList.remove('is-added'), 1600);
    }
  } catch (error) {
    reject(error);
    form.removeAttribute('data-terra-add');
    form.submit();
  }
});
